<template>
  <div class="app-initializing">
    <div class="init-card">
      <div v-if="isError" class="error-icon">⚠️</div>
      <div v-else class="spinner"></div>
      <h2>{{ stageTitle }}</h2>
      <p>{{ stageMessage }}</p>
      <div v-if="showProgress && !isError" class="progress-bar">
        <div class="progress-fill" :style="{ width: progress + '%' }"></div>
      </div>
      <button v-if="isError" class="retry-btn" @click="retry">{{ t('appInitializing.retry') }}</button>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  stage: {
    type: String,
    default: 'config',
    validator: (value) => ['config', 'database', 'auth', 'ready', 'error'].includes(value)
  },
  showProgress: {
    type: Boolean,
    default: false
  }
})

const stageConfig = computed(() => ({
  config: {
    title: t('appInitializing.configTitle'),
    message: t('appInitializing.configMessage'),
    progress: 25
  },
  database: {
    title: t('appInitializing.databaseTitle'),
    message: t('appInitializing.databaseMessage'),
    progress: 50
  },
  auth: {
    title: t('appInitializing.authTitle'),
    message: t('appInitializing.authMessage'),
    progress: 75
  },
  ready: {
    title: t('appInitializing.readyTitle'),
    message: t('appInitializing.readyMessage'),
    progress: 100
  },
  error: {
    title: t('appInitializing.errorTitle'),
    message: t('appInitializing.errorMessage'),
    progress: 0
  }
}))

const isError = computed(() => props.stage === 'error')
const stageTitle = computed(() => stageConfig.value[props.stage]?.title || t('appInitializing.loading'))
const stageMessage = computed(() => stageConfig.value[props.stage]?.message || '')
const progress = computed(() => stageConfig.value[props.stage]?.progress || 0)

function retry() {
  window.location.reload()
}
</script>

<style scoped>
.app-initializing {
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
}

.init-card {
  text-align: center;
  max-width: 400px;
}

.spinner {
  width: 48px;
  height: 48px;
  margin: 0 auto 1.5rem;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-top-color: #667eea;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.retry-btn {
  margin-top: 1rem;
  padding: 0.6rem 1.5rem;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  cursor: pointer;
}

.retry-btn:hover {
  background: #5a6fd6;
}

h2 {
  margin: 0 0 0.5rem;
  font-size: 1.25rem;
}

p {
  margin: 0 0 1rem;
  opacity: 0.7;
}

.progress-bar {
  height: 3px;
  background: rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: #667eea;
  transition: width 0.3s ease;
}
</style>
